import { reportError } from '@/instrumentation'
import { isAbortError } from '@/webgl/core/AbortSignalUtils'

/**
 * Reports a rejected playback promise, downgrading the refusals we expect to a console warning.
 *
 * Left unhandled these rejections escape the web component and get reported as crashes by the
 * host page's global `unhandledrejection` handler; reported to Sentry they are just noise, since
 * the browser refusing playback is a normal outcome and nothing is broken.
 *
 * Refusals are matched by `name` rather than instanceof: cross-realm DOMExceptions fail
 * instanceof. Aborts are benign everywhere — they mean playback was cut short by a pause() or an
 * unmount race — so callers only list the refusals specific to their own call.
 */
export function reportPlaybackRejection(
  e: unknown,
  { benignNames, warning, context }: { benignNames: string[]; warning: string; context: Record<string, unknown> }
): void {
  const name = (e as { name?: string } | null)?.name
  if ((name !== undefined && benignNames.includes(name)) || (e != null && isAbortError(e))) {
    console.warn(warning, e)
  } else {
    reportError(e, context)
  }
}

/**
 * Plays a media element without ever surfacing an unhandled rejection.
 *
 * `play()` rejects with `NotAllowedError` when autoplay is blocked (e.g. iOS Low Power Mode)
 * and `AbortError` when playback is interrupted by a pause() or an unmount race. Both are
 * benign, but left unhandled they escape the web component and get reported as crashes by the
 * host page's global `unhandledrejection` handler.
 */
export function safePlay(media: HTMLMediaElement | null | undefined): void {
  // Older WebKit returns undefined from play() instead of a promise, hence the optional chain.
  media?.play()?.catch((e: unknown) => {
    reportPlaybackRejection(e, {
      benignNames: ['NotAllowedError'],
      warning: 'safePlay: media playback refused by the browser',
      context: { feature: 'media-playback' },
    })
  })
}
